import { ImportedFileType } from '@databases/types/database';
import { cheerioIterator, parseHTML } from '@databases/utils/lib/dom';
import { Cheerio } from 'cheerio';
import { Element } from 'domhandler';

import { unreachable } from '../helpers/pasteHelper';
import { supportedPasteMimeTypes } from '../helpers/pasteHelper';
import { ParsedContentResult, ParsedPasteFile, PastedItem, RawFile } from '../types';

export const isTableInHtml = (rawContent: string) => {
  const cheerioDocument = parseHTML(rawContent);
  return getTableFromHtml(cheerioDocument).length > 0;
};

// Find a table in the content and extract its rows. The pasted content can contain a table element (with thead/tbody/tfoot children
// or direct tr children), or it can be a list of tr elements (the wrapping table is not part of the content), or it can be a list
// of th/td elements (in which case it represents a single table row).
const getTableFromHtml = (cheerioDocument: ReturnType<typeof parseHTML>) => {
  return cheerioDocument.find('table').eq(0);
};

function parseContent(file: RawFile): ParsedContentResult {
  switch (file.type) {
    case ImportedFileType.HTML: {
      const parsed = parseHTML(file.rawContent);

      const table = getTableFromHtml(parsed);

      const rowEls = [
        ...cheerioIterator(
          table.length === 0
            ? parsed.find('tr')
            : table.find('> tr,> thead > tr,> tbody > tr,> tfoot > tr')
        ),
      ];
      const rows: Array<Array<Cheerio<Element> | undefined>> = (
        rowEls.length === 0
          ? [parsed.find('th,td')]
          : rowEls.map((row) => row.children('th,td'))
      )
        .map((row) => [...cheerioIterator(row)])
        .filter((row) => row.length > 0);
      if (rows.length === 0) {
        const elements = parsed.find('p, span');
        const text = [...cheerioIterator(elements)]
          .map((element) => element.text())
          .join('\n');

        if (elements.length === 0) {
          return { success: false };
        } else {
          return {
            success: true,
            file: {
              name: file.name,
              mimeType: file.mimeType,
              rawContent: file.rawContent,
              size: file.size,
              type: ImportedFileType.CSV,
              content: text,
            },
          };
        }
      }

      for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
        const row = rows[rowIdx];

        for (const col of row) {
          if (col) {
            const colIdx = row.indexOf(col);

            const colspan = parseInt(col.attr('colspan') ?? '1');
            if (colspan > 1) {
              row.splice(colIdx + 1, 0, ...new Array(colspan - 1));
            }

            const rowspan = parseInt(col.attr('rowspan') ?? '1');
            if (rowspan > 1) {
              for (let i = rowIdx + 1; i < rowIdx + rowspan; i++) {
                rows[i]?.splice(colIdx, 0, undefined);
              }
            }
          }
        }
      }

      return {
        success: true,
        file: {
          content: rows,
          name: file.name,
          mimeType: file.mimeType,
          rawContent: file.rawContent,
          size: 0,
          type: file.type,
        },
      };
    }
    case ImportedFileType.CSV: {
      return {
        success: true,
        file: {
          content: file.rawContent,
          name: file.name,
          mimeType: file.mimeType,
          rawContent: file.rawContent,
          size: file.size,
          type: file.type,
        },
      };
    }
    case ImportedFileType.TEXT: {
      return {
        success: true,
        file: {
          content: file.rawContent,
          name: file.name,
          mimeType: file.mimeType,
          rawContent: file.rawContent,
          size: file.size,
          type: file.type,
        },
      };
    }
    default:
      return unreachable(file.type);
  }
}

function parseHtmlContent(htmlItem: PastedItem) {
  const file: RawFile = {
    name: `Pasted content (HTML)`,
    mimeType: htmlItem.type,
    rawContent: htmlItem.rawContent,
    size: 0,
    type: ImportedFileType.HTML,
  };
  const parsed = parseContent(file);
  if (parsed.success) {
    return parsed;
  }
}

function getFirstSuccessfulParsedItem(items: PastedItem[]) {
  for (const item of items) {
    const fileType = supportedPasteMimeTypes[item.type];
    if (item.rawContent) {
      const file: RawFile = {
        name: `Pasted content (${fileType})`,
        mimeType: item.type,
        rawContent: item.rawContent,
        size: 0,
        type: fileType,
      };
      const pasteContent = parseContent(file);
      if (pasteContent.success) {
        return pasteContent;
      }
    }
  }
}

function getHtmlItem(items: PastedItem[]) {
  return items.find((item) => item.type === 'text/html');
}

function getNonHtmlItemsInClipboard(items: PastedItem[]) {
  return items.filter((item) => item.type !== 'text/html');
}

/**
 * Takes pasted contents loaded by readPasteContent() and parses its contents.
 *
 * Returns HTML if it contains a table. If no table exists, it will return the first successfully parsed item before
 * returning the HTML. If none of the items can be parsed, it will throw an error.
 */
export function parsePasteContent(
  supportedItems: PastedItem[]
):
  | { success: true; file: ParsedPasteFile }
  | { success: false; error: { tag: 'PASTED_CONTENT_CANNOT_BE_READ' } } {
  // If HTML content exists in clipboard, parse it
  const firstHtmlItemInClipboard = getHtmlItem(supportedItems);
  let parsedHtmlContent: ParsedContentResult | undefined;
  if (firstHtmlItemInClipboard && firstHtmlItemInClipboard.rawContent) {
    parsedHtmlContent = parseHtmlContent(firstHtmlItemInClipboard);

    // If parsed HTML contains table, return it
    if (parsedHtmlContent && isTableInHtml(firstHtmlItemInClipboard.rawContent)) {
      return parsedHtmlContent;
    }
  }

  // If there isn't a table in the html, try parsing the other items in the clipboard.
  // Return the first successful parsed item.
  const otherItems = getNonHtmlItemsInClipboard(supportedItems);
  const firstSuccessfulParsedItem = getFirstSuccessfulParsedItem(otherItems);
  if (firstSuccessfulParsedItem) {
    return firstSuccessfulParsedItem;
  }

  // If the other items cannot be parsed, return the successfully parsed HTML.
  if (parsedHtmlContent?.success) {
    return parsedHtmlContent;
  }

  // Otherwise, throw an error.
  return {
    success: false,
    error: { tag: 'PASTED_CONTENT_CANNOT_BE_READ' },
  };
}
